import React from 'react';

import Button from '../../../components/ui/Button';

const InvoiceTable = ({ language, invoices, onViewDetails, onDownloadPDF, onRecordPayment }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'overdue':
        return 'bg-error/10 text-error';
      case 'partial':
        return 'bg-accent/10 text-accent';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      paid: language === 'fr' ? 'Payé' : language === 'ar' ? 'مدفوع' : 'Paid',
      pending: language === 'fr' ? 'En attente' : language === 'ar' ? 'قيد الانتظار' : 'Pending',
      overdue: language === 'fr' ? 'En retard' : language === 'ar' ? 'متأخر' : 'Overdue',
      partial: language === 'fr' ? 'Partiel' : language === 'ar' ? 'جزئي' : 'Partial'
    };
    return labels?.[status] || status;
  };

  const getBillingTypeLabel = (type) => {
    const labels = {
      flat_fee: language === 'fr' ? 'Forfaitaire' : language === 'ar' ? 'ثابت' : 'Flat Fee',
      hourly: language === 'fr' ? 'Horaire' : language === 'ar' ? 'بالساعة' : 'Hourly',
      per_hearing: language === 'fr' ? 'Par audience' : language === 'ar' ? 'لكل جلسة' : 'Per Hearing'
    };
    return labels?.[type] || type;
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat(language === 'ar' ? 'ar-MA' : 'fr-MA', {
      style: 'currency',
      currency: 'MAD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(language === 'ar' ? 'ar-MA' : 'fr-MA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })?.format(date);
  };

  const tableHeaders = {
    invoice: language === 'fr' ? 'N° Facture' : language === 'ar' ? 'رقم الفاتورة' : 'Invoice #',
    client: language === 'fr' ? 'Client' : language === 'ar' ? 'العميل' : 'Client',
    type: language === 'fr' ? 'Type' : language === 'ar' ? 'النوع' : 'Type',
    amount: language === 'fr' ? 'Montant' : language === 'ar' ? 'المبلغ' : 'Amount',
    status: language === 'fr' ? 'Statut' : language === 'ar' ? 'الحالة' : 'Status',
    dueDate: language === 'fr' ? 'Date d\'échéance' : language === 'ar' ? 'تاريخ الاستحقاق' : 'Due Date',
    actions: language === 'fr' ? 'Actions' : language === 'ar' ? 'الإجراءات' : 'Actions'
  };

  const actionLabels = {
    view: language === 'fr' ? 'Voir' : language === 'ar' ? 'عرض' : 'View',
    download: language === 'fr' ? 'Télécharger' : language === 'ar' ? 'تحميل' : 'Download',
    payment: language === 'fr' ? 'Paiement' : language === 'ar' ? 'دفع' : 'Payment'
  };

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                  {tableHeaders?.invoice}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                  {tableHeaders?.client}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                  {tableHeaders?.type}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                  {tableHeaders?.amount}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                  {tableHeaders?.status}
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                  {tableHeaders?.dueDate}
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">
                  {tableHeaders?.actions}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices?.map((invoice) => (
                <tr key={invoice?.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-4">
                    <span className="text-sm font-medium text-foreground monospace">
                      {invoice?.invoiceNumber}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-foreground">{invoice?.clientName}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-muted-foreground">
                      {getBillingTypeLabel(invoice?.billingType)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm font-semibold text-foreground monospace">
                      {formatAmount(invoice?.amount)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice?.status)}`}>
                      {getStatusLabel(invoice?.status)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-muted-foreground monospace">
                      {formatDate(invoice?.dueDate)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => onViewDetails(invoice)}
                        aria-label={actionLabels?.view}
                      >
                        {actionLabels?.view}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Download"
                        onClick={() => onDownloadPDF(invoice)}
                        aria-label={actionLabels?.download}
                      />
                      {invoice?.status !== 'paid' && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="CreditCard"
                          onClick={() => onRecordPayment(invoice)}
                        >
                          {actionLabels?.payment}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {invoices?.map((invoice) => (
          <div key={invoice?.id} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className="text-sm font-medium text-foreground monospace block">
                  {invoice?.invoiceNumber}
                </span>
                <span className="text-sm text-muted-foreground">
                  {invoice?.clientName}
                </span>
              </div>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice?.status)}`}>
                {getStatusLabel(invoice?.status)}
              </span>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{tableHeaders?.type}:</span>
                <span className="text-foreground">{getBillingTypeLabel(invoice?.billingType)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{tableHeaders?.amount}:</span>
                <span className="text-foreground font-semibold monospace">
                  {formatAmount(invoice?.amount)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{tableHeaders?.dueDate}:</span>
                <span className="text-foreground monospace">{formatDate(invoice?.dueDate)}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                onClick={() => onViewDetails(invoice)}
                fullWidth
              >
                {actionLabels?.view}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                onClick={() => onDownloadPDF(invoice)}
                aria-label={actionLabels?.download}
              />
              {invoice?.status !== 'paid' && (
                <Button
                  variant="default"
                  size="sm"
                  iconName="CreditCard"
                  onClick={() => onRecordPayment(invoice)}
                  fullWidth
                >
                  {actionLabels?.payment}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default InvoiceTable;